import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, CheckCircle2 } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@quebon/ui/components/alert'
import { Button } from '@quebon/ui/components/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@quebon/ui/components/dialog'
import { Field, FieldError, FieldLabel } from '@quebon/ui/components/field'
import { Spinner } from '@quebon/ui/components/spinner'
import { Textarea } from '@quebon/ui/components/textarea'

import type { TextbookRequestView } from '@/api/textbookRequests'

interface Props {
  request: TextbookRequestView
  nextStatus: 'RECEIVED' | 'CANCELLED'
  pending: boolean
  onClose: () => void
  onConfirm: (reason: string | null) => void
}

export function LcTextbookStatusActionDialog({ request, nextStatus, pending, onClose, onConfirm }: Props) {
  const { t } = useTranslation(['admin'])
  const [reason, setReason] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const requiresReason = nextStatus === 'CANCELLED'
  const reasonInvalid = submitted && requiresReason && !reason.trim()

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSubmitted(true)
    if (requiresReason && !reason.trim()) return
    onConfirm(reason.trim() || null)
  }

  const currentLabel = t(`admin:students.detail.textbookRequest.status.${request.status}`)
  const targetLabel = t(`admin:students.detail.textbookRequest.status.${nextStatus}`)

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !pending) onClose() }}>
      <DialogContent showCloseButton={!pending}>
        <form className="contents" onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>{t('admin:students.detail.textbookRequest.statusDialog.title')}</DialogTitle>
            <DialogDescription>
              {request.bookTitle ?? '-'}{request.volumeLabel ? ` (${request.volumeLabel})` : ''}
            </DialogDescription>
          </DialogHeader>
          <Alert variant={nextStatus === 'CANCELLED' ? 'destructive' : 'default'}>
            <AlertTriangle />
            <AlertTitle>{currentLabel} → {targetLabel}</AlertTitle>
            <AlertDescription>
              {t('admin:students.detail.textbookRequest.statusDialog.description')}
            </AlertDescription>
          </Alert>
          {requiresReason ? (
            <Field data-invalid={reasonInvalid || undefined}>
              <FieldLabel htmlFor="lc-textbook-cancel-reason">
                {t('admin:students.detail.textbookRequest.statusDialog.reasonLabel')}
              </FieldLabel>
              <Textarea
                id="lc-textbook-cancel-reason"
                value={reason}
                maxLength={1000}
                disabled={pending}
                aria-invalid={reasonInvalid || undefined}
                onChange={(event) => setReason(event.target.value)}
              />
              {reasonInvalid ? (
                <FieldError>{t('admin:students.detail.textbookRequest.statusDialog.reasonRequired')}</FieldError>
              ) : null}
            </Field>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" disabled={pending} onClick={onClose}>
              {t('admin:students.detail.textbookRequest.statusDialog.close')}
            </Button>
            <Button type="submit" variant={nextStatus === 'CANCELLED' ? 'destructive' : 'default'} disabled={pending}>
              {pending ? <Spinner data-icon="inline-start" /> : <CheckCircle2 data-icon="inline-start" />}
              {pending
                ? t('admin:students.detail.textbookRequest.statusDialog.processing')
                : t('admin:students.detail.textbookRequest.statusDialog.confirm')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
